import React, {useCallback} from 'react';

import {ColorPalette} from '../colorPalette/ColorPalette';
import {RunColorOptions, SemanticLegendSettings, YAxisValues} from '../types';
import {SemanticLegendBuckets} from './SemanticLegendBuckets';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';
import {SemanticLegendExcludedRunColor} from './SemanticLegendExcludedRunColor';
import {SemanticLegendKey} from './SemanticLegendKey';
import {SemanticLegendNumBuckets} from './SemanticLegendNumBuckets';
import {SemanticLegendRunColorOptions} from './SemanticLegendRunColorOptions';
import {
  SemanticLegendSettingsContextType,
  useSemanticLegendSettingsContext,
} from './SemanticLegendSettingsContextProvider';
import {SemanticLegendYValue} from './SemanticLegendYValue';

export const SemanticLegendColorTab = () => {
  const {
    hasActiveGrouping,
    configKeys,
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    isMetricBasedColors,
    updateSemanticLegendRunColorOptions,
    updateSemanticLegendSettingsOptimistically,
  } = useSemanticLegendSettingsContext() as SemanticLegendSettingsContextType; // We know this is this type because of the component fork

  const showMetricBasedColors = !hasActiveGrouping && isMetricBasedColors;

  const updateSetting = useCallback(
    (value: Partial<SemanticLegendSettings>) => {
      updateSemanticLegendSettingsOptimistically?.({
        ...semanticLegendSettings,
        ...value,
      });
    },
    [semanticLegendSettings, updateSemanticLegendSettingsOptimistically]
  );
  return (
    <div
      data-test="workspace-semantic-legend-color-settings"
      className={'flex h-full w-full flex-col gap-20 py-16'}>
      <SemanticLegendRunColorOptions
        hasActiveGrouping={hasActiveGrouping}
        runColorOptions={
          semanticLegendRunColorOptions ?? RunColorOptions.OriginalProjectColors
        }
        onRunColorOptionsChange={value => {
          updateSemanticLegendRunColorOptions?.(value);
        }}
      />
      {semanticLegendRunColorOptions ===
        RunColorOptions.OriginalProjectColors && <ColorPalette />}
      {showMetricBasedColors && (
        <div
          className={
            'ml-10 flex h-full w-full flex-col gap-10 border-l-2 border-moon-350'
          }>
          <SemanticLegendKey
            currentKey={semanticLegendSettings?.key ?? ''}
            type={
              semanticLegendSettings?.yAxisValue ??
              DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue
            }
            configKeys={configKeys}
            updateSetting={updateSetting}
          />
          {semanticLegendSettings?.key !==
            DEFAULT_SEMANTIC_LEGEND_SETTINGS.key &&
            semanticLegendSettings?.yAxisValue !==
              YAxisValues.Config_Number && (
              <SemanticLegendYValue
                yValue={
                  semanticLegendSettings?.yAxisValue ??
                  DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue
                }
                updateSetting={updateSetting}
              />
            )}
        </div>
      )}
      {showMetricBasedColors &&
        semanticLegendSettings?.key &&
        semanticLegendSettings?.key !==
          DEFAULT_SEMANTIC_LEGEND_SETTINGS.key && (
          <div className={'flex h-full w-full flex-col'}>
            <SemanticLegendNumBuckets
              numberOfBuckets={semanticLegendSettings?.numberOfBuckets ?? 0}
              updateSetting={updateSetting}
            />
            <SemanticLegendBuckets
              bucketCustomizations={
                semanticLegendSettings?.bucketCustomizations ?? {}
              }
              buckets={semanticLegendSettings?.buckets ?? []}
              updateSetting={updateSetting}
            />
            <SemanticLegendExcludedRunColor
              excludedRunColor={semanticLegendSettings?.excludedRunColor ?? ''}
              updateSetting={updateSetting}
            />
          </div>
        )}
    </div>
  );
};
